import { Preferences } from '@capacitor/preferences'
import { AuthTokens } from '@/types'

export class StorageService {
  private readonly JWT_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'matt_jwt_token'
  private readonly REFRESH_KEY = import.meta.env.VITE_REFRESH_TOKEN_KEY || 'matt_refresh_token'
  private readonly USER_KEY = 'matt_user_data'
  private readonly SETTINGS_KEY = 'matt_app_settings'

  /**
   * Store authentication tokens securely
   */
  async storeTokens(tokens: AuthTokens): Promise<void> {
    try {
      await Promise.all([
        Preferences.set({
          key: this.JWT_KEY,
          value: tokens.access
        }),
        Preferences.set({
          key: this.REFRESH_KEY,
          value: tokens.refresh
        })
      ])
    } catch (error) {
      console.error('Failed to store tokens:', error)
      throw new Error('Failed to store authentication tokens')
    }
  }

  /**
   * Retrieve authentication tokens
   */
  async getTokens(): Promise<AuthTokens | null> {
    try {
      const [accessResult, refreshResult] = await Promise.all([
        Preferences.get({ key: this.JWT_KEY }),
        Preferences.get({ key: this.REFRESH_KEY })
      ])

      if (accessResult.value && refreshResult.value) {
        return {
          access: accessResult.value,
          refresh: refreshResult.value
        }
      }
      return null
    } catch (error) {
      console.error('Failed to retrieve tokens:', error)
      return null
    }
  }

  /**
   * Get access token only
   */
  async getAccessToken(): Promise<string | null> {
    try {
      const result = await Preferences.get({ key: this.JWT_KEY })
      return result.value
    } catch (error) {
      console.error('Failed to retrieve access token:', error)
      return null
    }
  }

  /**
   * Get refresh token only
   */
  async getRefreshToken(): Promise<string | null> {
    try {
      const result = await Preferences.get({ key: this.REFRESH_KEY })
      return result.value
    } catch (error) {
      console.error('Failed to retrieve refresh token:', error)
      return null
    }
  }

  /**
   * Update access token only
   */
  async updateAccessToken(accessToken: string): Promise<void> {
    try {
      await Preferences.set({
        key: this.JWT_KEY,
        value: accessToken
      })
    } catch (error) {
      console.error('Failed to update access token:', error)
      throw new Error('Failed to update access token')
    }
  }

  /**
   * Clear authentication tokens
   */
  async clearTokens(): Promise<void> {
    try {
      await Promise.all([
        Preferences.remove({ key: this.JWT_KEY }),
        Preferences.remove({ key: this.REFRESH_KEY })
      ])
    } catch (error) {
      console.error('Failed to clear tokens:', error)
      throw new Error('Failed to clear authentication tokens')
    }
  }

  /**
   * Store user data
   */
  async storeUserData(userData: any): Promise<void> {
    try {
      await Preferences.set({
        key: this.USER_KEY,
        value: JSON.stringify(userData)
      })
    } catch (error) {
      console.error('Failed to store user data:', error)
      throw new Error('Failed to store user data')
    }
  }

  /**
   * Retrieve user data
   */
  async getUserData(): Promise<any | null> {
    try {
      const result = await Preferences.get({ key: this.USER_KEY })
      return result.value ? JSON.parse(result.value) : null
    } catch (error) {
      console.error('Failed to retrieve user data:', error)
      return null
    }
  }

  /**
   * Clear user data
   */
  async clearUserData(): Promise<void> {
    try {
      await Preferences.remove({ key: this.USER_KEY })
    } catch (error) {
      console.error('Failed to clear user data:', error)
      throw new Error('Failed to clear user data')
    }
  }

  /**
   * Store app settings
   */
  async storeSettings(settings: any): Promise<void> {
    try {
      await Preferences.set({
        key: this.SETTINGS_KEY,
        value: JSON.stringify(settings)
      })
    } catch (error) {
      console.error('Failed to store settings:', error)
      throw new Error('Failed to store settings')
    }
  }

  /**
   * Retrieve app settings
   */
  async getSettings(): Promise<any | null> {
    try {
      const result = await Preferences.get({ key: this.SETTINGS_KEY })
      return result.value ? JSON.parse(result.value) : null
    } catch (error) {
      console.error('Failed to retrieve settings:', error)
      return null
    }
  }

  /**
   * Store generic data with custom key
   */
  async store(key: string, data: any): Promise<void> {
    try {
      await Preferences.set({
        key,
        value: typeof data === 'string' ? data : JSON.stringify(data)
      })
    } catch (error) {
      console.error(`Failed to store data for key ${key}:`, error)
      throw new Error(`Failed to store data for key ${key}`)
    }
  }

  /**
   * Retrieve generic data with custom key
   */
  async retrieve(key: string): Promise<any | null> {
    try {
      const result = await Preferences.get({ key })
      if (!result.value) return null
      
      try {
        return JSON.parse(result.value)
      } catch {
        return result.value
      }
    } catch (error) {
      console.error(`Failed to retrieve data for key ${key}:`, error)
      return null
    }
  }

  /**
   * Remove data with custom key
   */
  async remove(key: string): Promise<void> {
    try {
      await Preferences.remove({ key })
    } catch (error) {
      console.error(`Failed to remove data for key ${key}:`, error)
      throw new Error(`Failed to remove data for key ${key}`)
    }
  }

  /**
   * Clear all stored data
   */
  async clearAll(): Promise<void> {
    try {
      await Preferences.clear()
    } catch (error) {
      console.error('Failed to clear all data:', error)
      throw new Error('Failed to clear all data')
    }
  }

  /**
   * Get all keys
   */
  async getAllKeys(): Promise<string[]> {
    try {
      const result = await Preferences.keys()
      return result.keys
    } catch (error) {
      console.error('Failed to get all keys:', error)
      return []
    }
  }
}

// Export singleton instance
export const storageService = new StorageService()
export default storageService